import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LEARNING_SUGGESTION_CHOICE,
  LEARNING_SUGGESTION_DECISION,
  LEARNING_SUGGESTION_DOMAIN_DECISION,
} from "@/constants";
import {
  decideLearningSuggestion,
  type LearningSuggestionChoice,
} from "@/features/chat/lib/chatLearningSuggestionApi";
import type { LearningSuggestion } from "@/features/chat/model/chatTypes";

type LearningSuggestionPromptProps = {
  suggestion?: LearningSuggestion;
  turnId?: string | null;
  /** False while the answer is streaming or was stopped: the choice buttons are hidden. */
  canAnswer: boolean;
};

const PRIMARY_BUTTON_SX = {
  textTransform: "none",
  bgcolor: "var(--theme-primary)",
  color: "var(--theme-text-on-primary)",
  "&:hover": { bgcolor: "var(--theme-hover-primary)" },
} as const;

const SECONDARY_BUTTON_SX = {
  textTransform: "none",
  color: "var(--theme-text-secondary)",
} as const;

/**
 * Under an answer whose question stated a correction or preference: offers to save it as the
 * user's learning, then to suggest it for the whole domain. Each choice is stored on the turn,
 * so a reloaded chat shows where the user left off rather than asking again.
 */
export function LearningSuggestionPrompt({
  suggestion,
  turnId,
  canAnswer,
}: LearningSuggestionPromptProps) {
  const { t } = useTranslation("chat");
  const [current, setCurrent] = useState(suggestion);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCurrent(suggestion);
  }, [suggestion]);

  if (!current || current.decision === LEARNING_SUGGESTION_DECISION.DISMISSED) {
    return null;
  }

  const choose = async (choice: LearningSuggestionChoice) => {
    if (!turnId) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      setCurrent(await decideLearningSuggestion(turnId, choice));
    } catch {
      setError(t("learningSuggestion.saveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const showButtons = canAnswer && Boolean(turnId);
  const isSaved = current.decision === LEARNING_SUGGESTION_DECISION.SAVED;
  const question = isSaved
    ? current.domainDecision === null
      ? t("learningSuggestion.domainQuestion")
      : null
    : t("learningSuggestion.saveQuestion");
  const choices: [LearningSuggestionChoice, string][] = isSaved
    ? [
        [LEARNING_SUGGESTION_CHOICE.SUGGEST_TO_DOMAIN, t("learningSuggestion.suggest")],
        [LEARNING_SUGGESTION_CHOICE.KEEP_PERSONAL, t("learningSuggestion.keepPersonal")],
      ]
    : [
        [LEARNING_SUGGESTION_CHOICE.SAVE, t("learningSuggestion.save")],
        [LEARNING_SUGGESTION_CHOICE.DISMISS, t("learningSuggestion.dismiss")],
      ];

  return (
    <Box
      data-testid="learning-suggestion"
      sx={{
        mt: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: "1px dashed var(--theme-border)",
        bgcolor: "var(--theme-background)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <LightbulbOutlined
          sx={{ fontSize: 18, mt: 0.25, color: "var(--theme-primary-light)", flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {isSaved ? (
            <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
              {current.domainDecision === LEARNING_SUGGESTION_DOMAIN_DECISION.SUGGESTED
                ? t("learningSuggestion.savedAndSuggested")
                : t("learningSuggestion.saved")}
            </Typography>
          ) : null}
          {question ? (
            <Typography variant="caption" sx={{ display: "block", fontWeight: isSaved ? 400 : 600 }}>
              {question}
            </Typography>
          ) : null}
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", color: "var(--theme-text-secondary)", mt: 0.25 }}
          >
            “{current.statement}”
          </Typography>
          {question && showButtons ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {choices.map(([choice, label], index) => (
                <Button
                  key={choice}
                  size="small"
                  variant={index === 0 ? "contained" : "text"}
                  disabled={isSubmitting}
                  onClick={() => void choose(choice)}
                  sx={index === 0 ? PRIMARY_BUTTON_SX : SECONDARY_BUTTON_SX}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          ) : null}
          {error ? (
            <Typography
              variant="caption"
              role="alert"
              sx={{ display: "block", mt: 0.5, color: "var(--theme-error)" }}
            >
              {error}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
